// ** Modules
import Image from 'next/image';
import type { MouseEventHandler } from 'react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import { Icon } from '@/components/common/icons';

export const burgerHoverSx = {
	'@media (hover: hover) and (pointer: fine)': {
		'& .burger-icon__line': {
			transition: 'transform 180ms ease',
			transformBox: 'fill-box',
			transformOrigin: 'center'
		},
		'&:hover .burger-icon__line--top': { transform: 'translateX(2px)' },
		'&:hover .burger-icon__line--middle': { transform: 'translateX(-2px)' },
		'&:hover .burger-icon__line--bottom': { transform: 'translateX(2px)' },
		'@media (prefers-reduced-motion: reduce)': {
			'& .burger-icon__line': { transition: 'none' },
			'&:hover .burger-icon__line': { transform: 'none' }
		}
	}
} as const;

export type HeaderLogoProps = {
	logoSrc: string;
	logoAlt?: string;
	sx?: Record<string, any>;
};

export const HeaderLogo = ({ sx, logoSrc, logoAlt = 'Logo' }: HeaderLogoProps) => {
	const theme = useTheme();

	return (
		<Box
			sx={{
				position: 'absolute',
				top: 24,
				left: {
					xs: theme.spacing(theme.layout.pagePx.xs),
					md: theme.spacing(theme.layout.pagePx.md),
					lg: theme.spacing(theme.layout.pagePx.lg)
				},
				width: { xs: 72, md: 81 },
				height: { xs: 64, md: 72 },
				aspectRatio: '9 / 8',
				...(sx ?? {})
			}}
		>
			<Image
				src={logoSrc}
				alt={logoAlt}
				fill
				priority
				sizes="(max-width: 900px) 72px, 81px"
				style={{ objectFit: 'contain' }}
			/>
		</Box>
	);
};

export type HeaderMenuButtonProps = {
	label?: string;
	iconName?: 'burger' | 'close';
	color?: string;
	show?: boolean;
	showOnMobile?: boolean;
	hideLabelOnMobile?: boolean;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	sx?: Record<string, any>;
};

export const HeaderMenuButton = ({
	label = 'Menu',
	iconName = 'burger',
	color,
	show = true,
	showOnMobile = false,
	hideLabelOnMobile = false,
	onClick,
	sx
}: HeaderMenuButtonProps) => {
	const theme = useTheme();

	if (!show) return null;

	const resolvedColor = color ?? theme.palette.common.white;

	return (
		<Box
			component="button"
			type="button"
			onClick={onClick}
			sx={{
				position: 'absolute',
				top: 24,
				right: {
					xs: theme.spacing(theme.layout.pagePx.xs),
					md: theme.spacing(theme.layout.pagePx.md),
					lg: theme.spacing(theme.layout.pagePx.lg)
				},
				display: showOnMobile ? { xs: 'flex', md: 'flex' } : { xs: 'none', md: 'flex' },
				alignItems: 'center',
				gap: 2,
				border: 0,
				background: 'transparent',
				padding: 0,
				cursor: 'pointer',
				...burgerHoverSx,
				...(sx ?? {})
			}}
		>
			<Typography
				variant="body-md"
				sx={{
					color: resolvedColor,
					textAlign: 'right',
					...(hideLabelOnMobile ? { display: { xs: 'none', sm: 'block' } } : null)
				}}
			>
				{label}
			</Typography>
			<Icon name={iconName} color={resolvedColor} />
		</Box>
	);
};
